import React from 'react';

export interface HeroCardStat {
    label: string;
    val: string | number;
}

export interface HeroCardLegendItem {
    label: string;
    val: string | number;
    colorClass: string;
}

export interface HeroCardRingSegment {
    /** stroke-dasharray, e.g. "130 263" (out of the ~263 circumference). */
    dasharray: string;
    /** stroke-dashoffset, e.g. "-130". Omit for the first segment. */
    dashoffset?: string;
    /** Tailwind stroke-* class declared in the theme, e.g. "stroke-success". */
    strokeClass: string;
}

export interface HeroCardProps {
    title?: string;
    badge?: string;
    periodPrimary?: string;
    periodSecondary?: string;
    stats?: HeroCardStat[];
    score?: string | number;
    scoreLabel?: string;
    ringSegments?: HeroCardRingSegment[];
    legend?: HeroCardLegendItem[];
}

const HeroCard: React.FC<HeroCardProps> = ({
    title,
    badge,
    periodPrimary,
    periodSecondary,
    stats = [],
    score,
    scoreLabel,
    ringSegments = [],
    legend = [],
}) => {
    const showScoreBlock = score != null || scoreLabel || ringSegments.length > 0 || legend.length > 0;

    return (
        <div className={`w-full rounded-theme p-4 sm:p-6 lg:p-8 text-white flex flex-col lg:flex-row items-stretch lg:items-center justify-between shadow-xl overflow-hidden min-h-[220px] hover:shadow-theme-hover hover:-translate-y-1 transition-all duration-300 bg-primary-600 gap-6 lg:gap-0`}>
            <div className="flex-1 flex flex-col justify-between h-full">
                <div>
                    {(title || badge) && (
                        <div className="flex flex-wrap items-center gap-3 mb-4">
                            {title ? (
                                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{title}</h2>
                            ) : null}
                            {badge ? (
                                <span className="px-3 py-1 bg-white/20 rounded-full text-xxs font-black tracking-widest uppercase">{badge}</span>
                            ) : null}
                        </div>
                    )}
                    {(periodPrimary || periodSecondary) && (
                        <div className="space-y-1 mb-6 sm:mb-8">
                            {periodPrimary ? <p className="text-white/60 text-xs font-medium">{periodPrimary}</p> : null}
                            {periodSecondary ? <p className="text-white/50 text-[10px] font-bold">{periodSecondary}</p> : null}
                        </div>
                    )}
                </div>

                {stats.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-8">
                        {stats.map((stat) => (
                            <div key={stat.label}>
                                <p className="text-[9px] font-black text-white/40 uppercase mb-1.5 tracking-wider whitespace-nowrap">{stat.label}</p>
                                <p className="text-lg sm:text-xl lg:text-2xl font-black">{stat.val}</p>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {showScoreBlock && (
                <div className="flex flex-col sm:flex-row items-center gap-8 lg:gap-10 lg:pl-10 lg:border-l border-white/10 pt-8 lg:pt-0 border-t lg:border-t-0">
                    {(score != null || scoreLabel || ringSegments.length > 0) && (
                        <div className="relative w-32 h-32 sm:w-40 sm:h-40 shrink-0">
                            {(score != null || scoreLabel) && (
                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    {score != null ? (
                                        <span className="text-3xl sm:text-5xl font-black">{score}</span>
                                    ) : null}
                                    {scoreLabel ? (
                                        <span className="text-[7px] sm:text-[8px] font-black tracking-widest text-white/40 uppercase">{scoreLabel}</span>
                                    ) : null}
                                </div>
                            )}
                            <svg className="w-full h-full -rotate-90 transform" viewBox="0 0 100 100">
                                <circle cx="50" cy="50" r="42" stroke="currentColor" strokeWidth="12" fill="transparent" className="text-white/5" />
                                {ringSegments.map((seg, i) => (
                                    <circle
                                        key={i}
                                        cx="50"
                                        cy="50"
                                        r="42"
                                        strokeWidth="12"
                                        strokeDasharray={seg.dasharray}
                                        strokeDashoffset={seg.dashoffset}
                                        fill="transparent"
                                        className={seg.strokeClass}
                                    />
                                ))}
                            </svg>
                        </div>
                    )}

                    {legend.length > 0 && (
                        <div className="flex flex-col gap-2 sm:gap-3 w-full sm:min-w-[150px]">
                            {legend.map((item) => (
                                <div key={item.label} className="flex items-center justify-between group cursor-default">
                                    <div className="flex items-center gap-2.5">
                                        <div className={`w-2 h-2 rounded-full shadow-[0_0_8px_rgba(0,0,0,0.2)] ${item.colorClass}`} />
                                        <span className="text-[10px] sm:text-xxs font-bold text-white/60 group-hover:text-white transition-colors">{item.label}:</span>
                                    </div>
                                    <span className="text-[10px] sm:text-[11px] font-black">{item.val}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default HeroCard;
